// TarotResultCarousel.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

const GOLD = "#FFD86E";
const GOLD_GRAD =
  "linear-gradient(90deg,#FFD86E 0%, #EAAE2C 45%, #CF7B00 100%)";

function InfoBlock({ icon, title, children }) {
  return (
    <section className="mt-5">
      <div className="relative rounded-[22px]">
        <div
          className="rounded-[22px] border px-5 sm:px-6 py-5 sm:py-6"
          style={{
            borderColor: "rgba(255,255,255,0.6)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <h3 className="flex items-center gap-2 text-[18px] sm:text-[20px] font-extrabold">
            <span className="inline-flex w-6 h-6 items-center justify-center">
              {icon}
            </span>
            {title}
          </h3>
          <p className="text-[14px] sm:text-[15px] leading-6 mt-2 text-white/90 text-left whitespace-pre-line">
            {children}
          </p>
        </div>
      </div>
    </section>
  );
}

// ===== helpers =====
const positionKey = (i) =>
  i === 0 ? "past" : i === 1 ? "present" : i === 2 ? "future" : "present";

const PHASE_LABEL = {
  past: "อดีต (Past)",
  present: "ปัจจุบัน (Present)",
  future: "อนาคต (Future)",
};

// ตรวจว่าเป็น object แท้ (ไม่ใช่ array/primitive)
const isPlainObject = (v) => !!v && typeof v === "object" && !Array.isArray(v);

// ดึงค่าแบบไล่ชั้น: phase.field -> present.field -> prediction/text -> default
const cascadeGet = (card, phaseKey, field) => {
  const phaseSeg = isPlainObject(card?.[phaseKey]) ? card[phaseKey] : null;
  const presentSeg = isPlainObject(card?.present) ? card.present : null;

  return (
    phaseSeg?.[field] ??
    presentSeg?.[field] ??
    card?.prediction ??
    card?.text ??
    "ยังไม่มีคำทำนายเฉพาะช่วงเวลา"
  );
};

// สร้าง segment (work/money/love/health/advice) สำหรับช่วงเวลานั้น ๆ โดยเติมเฉพาะช่องที่ขาด
const buildSegment = (card, idx) => {
  const key = positionKey(idx);

  return {
    work: cascadeGet(card, key, "work"),
    money: cascadeGet(card, key, "money"),
    love: cascadeGet(card, key, "love"),
    health: cascadeGet(card, key, "health"),
    advice: cascadeGet(card, key, "advice"),
  };
};

export default function TarotResultCarousel() {
  const navigate = useNavigate();
  const { state } = useLocation();

  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "view_tarot_three_card_result",
      category: "Tarot",
      label: "view_tarot_three_card_result",
    });
  }, []);

  // ===== data =====
  const fallback = [
    {
      id: 64,
      card: "Ace of Wands (ไม้เท้า 1)",
      image: "/images/tarot/aceofwands.jpeg",
      text: "ไฟเริ่มต้น พลัง และความกล้าลงมือทำ",
      prediction: "โอกาสใหม่ ความกระตือรือร้น จุดไฟให้โปรเจกต์/ความสัมพันธ์",
      tags: ["เริ่มต้น", "พลังงาน", "โอกาส"],
    },
    {
      id: 6,
      card: "The Lovers",
      image: "/images/tarot/thelovers.jpeg",
      text: "ความรัก การจับคู่ การตัดสินใจสำคัญ",
      prediction:
        "สายสัมพันธ์ที่ลึกซึ้ง หรือถึงเวลาต้องเลือกด้วยหัวใจกับเหตุผล",
      tags: ["รัก", "คู่", "การตัดสินใจ"],
    },
    {
      id: 17,
      card: "The Star",
      image: "/images/tarot/thestar.jpeg",
      text: "ความหวัง การเยียวยา ความสงบ",
      prediction: "แสงนำทางหลังพายุ โอกาสสมหวังและใจที่กลับมาสงบ",
      tags: ["ความหวัง", "สมหวัง", "เยียวยา"],
    },
  ];

  const cards = useMemo(
    () =>
      Array.isArray(state?.cards) && state.cards.length
        ? state.cards
        : fallback,
    [state]
  );

  const [idx, setIdx] = useState(0);
  const current = cards[idx] ?? cards[0];

  const canPrev = idx > 0;
  const canNext = idx < cards.length - 1;

  const goPrev = useCallback(() => {
    if (canPrev) setIdx((i) => i - 1);
  }, [canPrev]);

  const goNext = useCallback(() => {
    if (canNext) setIdx((i) => i + 1);
  }, [canNext]);

  // keyboard arrows
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  // touch swipe (มือถือ)
  const touchStartX = useRef(null);
  const onTouchStart = (e) => (touchStartX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      dx > 0 ? goPrev() : goNext();
    }
    touchStartX.current = null;
  };

  // ===== phase & segment =====
  const phaseKey = positionKey(idx);
  const phaseLabel = PHASE_LABEL[phaseKey] || "ปัจจุบัน (Present)";
  const segment = buildSegment(current, idx);

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white">
      {/* Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
        }}
      >
        <div className="w-full h-16 px-5 py-2 flex items-end">
          <button
            aria-label="ย้อนกลับ"
            onClick={() => navigate("/horoscope/tarot")}
            className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 19L8 12L15 5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Content */}
      <main
        className="px-6 pt-16 pb-28 max-w-[720px] mx-auto"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* จุดแสดงความคืบหน้า */}
        <div className="mt-6 flex justify-center gap-2">
          {cards.map((_, i) => (
            <button
              key={i}
              className="rounded-full transition-all"
              style={{
                width: i === idx ? 18 : 8,
                height: 8,
                background: i === idx ? GOLD : "rgba(255,255,255,.35)",
              }}
              aria-label={`ไปยังใบที่ ${i + 1}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>

        {/* ชื่อ + ตัวนับ + ช่วงเวลา */}
        <div className="text-center mt-6">
          <div className="mt-2 text-white/70 text-[16px]">
            ใบที่ <span style={{ color: GOLD }}>{idx + 1}</span> /{" "}
            {cards.length}
          </div>
          <div className="mt-3">
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-[18px] border"
              style={{ borderColor: "rgba(255,255,255,.5)" }}
            >
              ช่วงเวลา:{" "}
              <span className="ml-1" style={{ color: GOLD }}>
                {phaseLabel}
              </span>
            </span>
          </div>
        </div>
        {/* รูปไพ่ */}
        <div className="flex justify-center mt-4">
          <div className="rounded-[18px] overflow-hidden shadow-[0_14px_36px_rgba(0,0,0,0.45)]">
            <img
              src={current.image}
              alt={current.card}
              className="w-[180px] sm:w-[200px] h-auto object-cover"
              loading="lazy"
            />
          </div>
        </div>

        {/* ชื่อ */}
        <div className="text-center mt-6">
          <h1 className="text-[22px] sm:text-[26px] font-extrabold">
            {current.card}
          </h1>
        </div>

        {/* ความหมายของไพ่ (static meaning) */}
        <InfoBlock
          title="ความหมายของไพ่"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {current.text ||
            "เป็นสัญลักษณ์ของความสำเร็จ ความมั่นคง และความอุ่นใจ พร้อมพาโอกาสดี ๆ เข้ามา"}
        </InfoBlock>

        {/* คำทำนายแยกหัวข้อ ตามช่วงเวลา */}
        <InfoBlock
          title="งาน"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M4 7h16v12H4z" stroke="white" strokeWidth="1.6" />
              <path d="M9 7V5h6v2" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {segment.work}
        </InfoBlock>

        <InfoBlock
          title="การเงิน"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <circle cx="12" cy="12" r="8" stroke="white" strokeWidth="1.6" />
              <path d="M8 12h8M12 8v8" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {segment.money}
        </InfoBlock>

        <InfoBlock
          title="ความรัก"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path
                d="M12 21s-7-4.35-7-9a4 4 0 0 1 7-2.65A4 4 0 0 1 19 12c0 4.65-7 9-7 9Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {segment.love}
        </InfoBlock>

        <InfoBlock
          title="สุขภาพ"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <rect
                x="4"
                y="4"
                width="16"
                height="16"
                rx="3"
                stroke="white"
                strokeWidth="1.6"
              />
              <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="1.6" />
            </svg>
          }
        >
          {segment.health}
        </InfoBlock>

        <InfoBlock
          title="คำแนะนำ"
          icon={
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
              <path d="M9 18h6M10 21h4" stroke="white" strokeWidth="1.6" />
              <path
                d="M12 3a7 7 0 0 1 7 7c0 2.2-1.1 3.8-2.6 5.1-.7.6-1.2 1.2-1.4 1.9h-6c-.2-.7-.7-1.3-1.4-1.9C6.1 13.8 5 12.2 5 10a7 7 0 0 1 7-7Z"
                stroke="white"
                strokeWidth="1.6"
              />
            </svg>
          }
        >
          {segment.advice}
        </InfoBlock>

        {/* ปุ่มควบคุม */}
        <div className="max-w-[720px] mx-auto px-6 py-3 flex gap-3 mt-10">
          <button
            onClick={goPrev}
            disabled={!canPrev}
            className="flex-1 h-11 rounded-full font-bold disabled:opacity-40"
            style={{ border: "1.5px solid rgba(255,255,255,.5)" }}
          >
            ก่อนหน้า
          </button>
          <button
            onClick={goNext}
            disabled={!canNext}
            className="flex-1 h-11 rounded-full font-bold disabled:opacity-40"
            style={{ border: "1.5px solid rgba(255,255,255,.5)" }}
          >
            ถัดไป
          </button>
        </div>
      </main>
    </div>
  );
}
